import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import { registerLocale } from "react-datepicker";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_STATUS_ACTIVE_DISABLE,
  LIST_CURRENCY_OPTION,
  STATUS_ACTIVE_DISABLE_OPTION,
} from "helpers/constans";
import PlanFeatureArray from "components/Common/PlanFeatureArray";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { toast } from "react-toastify";
import { createPlan, updatePlan } from "store/plans/thunk";
import { NumericFormat } from "react-number-format";
import plansApi from "api/planApi";
import { ColorPicker } from "antd";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const PlanForm = ({
  id = "",
  item,
  triggerRefresh,
  isShowDetail = false,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  // console.log(item)
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);
  const [touchedDiscountRate, setTouchedDiscountRate] = useState(false);
  const [touchedPriceFinal, setTouchedPriceFinal] = useState(false);


  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const data = {
        ...values,
        isActive: values?.isActive === 1 ? true : false,
        vatRate: Number(values?.vatRate || null),
      };

      const response: any = id
        ? await updatePlan(id, data)
        : await createPlan(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    code: Yup.string().required(`${t("This field is required")}`),
    name: Yup.string().required(`${t("This field is required")}`),
    description: Yup.string().required(`${t("This field is required")}`),
    features: Yup.array()
      .of(
        Yup.object({
          limitType: Yup.string().required(t("This field is required")),
          key: Yup.string().required(t("This field is required")),
          durationDays: Yup.number().min(0, t("Please enter an amount greater than 0")).nullable(),
          // maxUsage: Yup.number().min(0, t("Please enter an amount greater than 0")).nullable(),
          maxUsage: Yup.number().nullable()
            .when(["limitType", "key"], ([limitType, key], schema) => {
              return (limitType === "QUOTA" && key !== "POST_JOB")
                ? schema
                    .required(t("This field is required"))
                    .moreThan(0, t("Please enter an amount greater than 0"))
                : schema;
            }),

          // maxUsage: Yup.number()
          //   .nullable()
          //   .when("key", ([key], schema) => {
          //     return key === "JUMP_RANK"
          //       ? schema
          //           .required(t("This field is required"))
          //           .moreThan(0, t("Please enter an amount greater than 0"))
          //       : schema;
          //   }),
          // intervalMinutes: Yup.number()
          //   .nullable()
          //   .when("key", ([key], schema) => {
          //     return key === "JUMP_RANK"
          //       ? schema
          //           .required(t("This field is required"))
          //           .moreThan(0, t("Please enter an amount greater than 0"))
          //       : schema;
          //   }),
          extra: Yup.object({
            maxUsagePerDay: Yup.number()
              .nullable()
              .test("required-if-jump-rank0", t("This field is required"), function (value) {
                const parent = this?.from?.[1]?.value; // Truy cập object cha trong mảng features
                const key = parent?.key;

                if (key === "JUMP_RANK") {
                  return value != null && value > 0;
                }

                return true;
              }),
            intervalMinutes: Yup.number()
              .nullable()
              .test("required-if-jump-rank1", t("This field is required"), function (value) {
                const parent = this?.from?.[1]?.value; // Truy cập object cha trong mảng features
                const key = parent?.key;

                if (key === "JUMP_RANK") {
                  return value != null && value > 0;
                }

                return true;
              }),
            actionCost: Yup.number()
              .nullable()
              .test("required-if-jump-rank2", t("This field is required"), function (value) {
                const parent = this?.from?.[1]?.value; // Truy cập object cha trong mảng features
                const key = parent?.key;

                if (key === "JUMP_RANK") {
                  return value != null && value > 0;
                }

                return true;
              }),
          }),
        })
      )
      .min(1, t("This field is required")),
    priceType: Yup.string().oneOf(["CUSTOM", "FREE"]).required(t("This field is required")),
    price: Yup.number().when("priceType", ([priceType], schema) => {
      return priceType === "CUSTOM"
        ? schema
            .typeError(t("Price must be a number"))
            .required(t("This field is required"))
            .moreThan(0, t("Please enter an amount greater than 0"))
        : schema.notRequired();
    }),
    vatRate: Yup.number().when("priceType", ([priceType], schema) => {
      return priceType === "CUSTOM"
        ? schema
            .typeError(t("VAT must be a number"))
            .required(t("This field is required"))
        : schema.notRequired();
    }),
    currency: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      code: "",
      name: "",
      description: "",
      features: [
        {
          limitType: "",
          key: "",
          maxUsage: undefined,
          durationDays: undefined,
          extra: {
            actionCost: 1,
            maxUsagePerDay: 1,
            intervalMinutes: 10,
          },
          id: 1,
        },
      ] as any[],
      // badgeInfo: {
      //   content: "",
      //   textColor: "",
      //   textColorSelected: "",
      //   bgColor: "",
      //   bgColorSelected: "",
      // },
      price: 0,
      discountRate: null,
      priceFinal: 0,
      vatRate: 0,
      priceType: "CUSTOM" as "CUSTOM" | "FREE",
      currency: "KRW",
      isActive: 1,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  const handleSetValueForm = (value: any) => {
    const features = value?.features?.map((item: any) => ({
      id: item?.id,
      key: item?.key,
      limitType: item?.limitType,
      maxUsage: item?.maxUsage,
      durationDays: item?.durationDays,
      ...(item?.key === "JUMP_RANK" ? {
        extra: {
          maxUsagePerDay: item?.extra?.maxUsagePerDay,
          actionCost: item?.extra?.actionCost,
          intervalMinutes: item?.extra?.intervalMinutes,
        },
      }: null)
    }));

    const data = {
      code: value?.code,
      name: value?.info?.name,
      description: value?.info?.description,
      discountRate: value?.price?.discountRate,
      priceFinal: value?.price?.priceFinal,
      price: value?.price?.price,
      vatRate: value?.price?.vatRate,
      priceType: value?.priceType,
      currency: value?.price?.currency,
      isActive: value?.isActive === true ? 1 : 0,
      features,
      // badgeInfo: value?.badgeInfo,
    }
    formik.setValues(data);
  }

  useEffect(() => {
    if(id) {
      const handlePlanDetail = async () => {
        try {
          const response: any = await plansApi.getPlanDetail(item?.id);
          if(response?.data) {
            handleSetValueForm(response?.data)
          }
        }catch(error) {
          console.log(error)
        }
      }
      handlePlanDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Plan")} - ${t("Plan Management")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);
  
  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Plan") : t("Create Plan")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 7) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )}
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="code" className="form-limitType">
                        {t("Plan Code")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="code"
                        name="code"
                        type="text"
                        className="form-control"
                        placeholder={t("Plan Code")}
                        value={formik?.values?.code}
                        onChange={(event: any) =>
                          formik.setFieldValue("code", event?.target?.value)
                        }
                        disabled={!!id}
                      />
                      {formik.touched.code && formik.errors.code ? (
                        <div className="text-danger mt-2">
                          {formik.errors.code}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Plan Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder={t("Plan Name")}
                        value={formik?.values?.name}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="description" className="form-label">
                        {t("Plan description")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="description"
                        name="description"
                        type="textarea"
                        rows="5"
                        className="form-control"
                        placeholder={t("Enter the description")}
                        value={formik?.values?.description || ""}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "description",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.description &&
                      formik.errors.description ? (
                        <div className="text-danger mt-2">
                          {formik.errors.description}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  {/* <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="communityDelayRange" className="form-label">
                        {t("Badge Info")}
                      </Label>

                      <Row>
                        <Col lg={3}>
                          <div className="mb-1">
                            <Label htmlFor="content" className="form-label">
                              {t("Label Text")}
                            </Label>
                            <Input
                              name="name"
                              type="text"
                              className="form-control"
                              placeholder={t("Label Text")}
                              value={formik?.values?.badgeInfo?.content}
                              onChange={(event) => formik.setFieldValue("name", event?.target?.value)}
                            />
                            {formik.touched?.badgeInfo?.content && formik.errors?.badgeInfo?.content ? (
                              <div className="text-danger mt-2">
                                {formik.errors?.badgeInfo?.content}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={2} className="mt-2">
                          <label className="form-label w-100">
                            {`${t("Text Color")}`}
                          </label>
                          <ColorPicker
                            defaultValue="#1677ff"
                            showText
                            allowClear
                            onChange={(value) => {
                              // formik.setFieldValue(`color`, value)
                              let color = typeof value == "string"
                              ? value
                              : value?.toHexString()
                              formik.setFieldValue(`badgeInfo.textColor`, color)
                            }}
                            value={formik.values?.badgeInfo?.textColor}
                          />{" "}
                          {formik.touched.badgeInfo?.textColor && formik.errors.badgeInfo?.textColor ? (
                            <div className="text-danger mt-2">{formik.errors.badgeInfo?.textColor}</div>
                          ) : null}
                        </Col>
                        <Col lg={2} className="mt-2">
                          <label className="form-label w-100">
                            {`${t("Text Color Selected")}`}
                          </label>
                          <ColorPicker
                            defaultValue="#1677ff"
                            showText
                            allowClear
                            onChange={(value) => {
                              // formik.setFieldValue(`color`, value)
                              let color = typeof value == "string"
                              ? value
                              : value?.toHexString()
                              formik.setFieldValue(`badgeInfo.textColorSelected`, color)
                            }}
                            value={formik.values?.badgeInfo?.textColorSelected}
                          />{" "}
                          {formik.touched.badgeInfo?.textColorSelected && formik.errors.badgeInfo?.textColorSelected ? (
                            <div className="text-danger mt-2">{formik.errors.badgeInfo?.textColorSelected}</div>
                          ) : null}
                        </Col>
                      <Col lg={2} className="mt-2">
                          <label className="form-label w-100">
                            {`${t("Background Color")}`}
                          </label>
                          <ColorPicker
                            defaultValue="#1677ff"
                            showText
                            allowClear
                            onChange={(value) => {
                              // formik.setFieldValue(`color`, value)
                              let color = typeof value == "string"
                              ? value
                              : value?.toHexString()
                              formik.setFieldValue(`badgeInfo.bgColor`, color)
                            }}
                            value={formik.values?.badgeInfo?.bgColor}
                          />{" "}
                          {formik.touched.badgeInfo?.bgColor && formik.errors.badgeInfo?.bgColor ? (
                            <div className="text-danger mt-2">{formik.errors.badgeInfo?.bgColor}</div>
                          ) : null}
                        </Col>
                        <Col lg={3} className="mt-2">
                          <label className="form-label w-100">
                            {`${t("Background Color Selected")}`}
                          </label>
                          <ColorPicker
                            defaultValue="#1677ff"
                            showText
                            allowClear
                            onChange={(value) => {
                              // formik.setFieldValue(`color`, value)
                              let color = typeof value == "string"
                              ? value
                              : value?.toHexString()
                              formik.setFieldValue(`badgeInfo.bgColorSelected`, color)
                            }}
                            value={formik.values?.badgeInfo?.bgColorSelected}
                          />{" "}
                          {formik.touched.badgeInfo?.bgColorSelected && formik.errors.badgeInfo?.bgColorSelected ? (
                            <div className="text-danger mt-2">{formik.errors.badgeInfo?.bgColorSelected}</div>
                          ) : null}
                        </Col>
                      </Row>
                    </div>
                  </Col> */}

                  <Col md={12} lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="priceType" className="form-label">
                        {t("Price Type")}
                      </Label>
                      <div className="d-flex justify-content-start align-items-center">
                        <div
                          className="d-flex justify-content-start align-items-center form-check me-3"
                        >
                          <input
                            className="form-check-input me-2"
                            type="radio"
                            name="custom-price"
                            id={`custom-price-1`}
                            checked={formik?.values?.priceType === "CUSTOM"}
                            style={{ minWidth: "16px" }}
                            onChange={(event) => {
                              formik.setFieldValue("priceType", "CUSTOM")
                            }}
                          />
                          <label
                            className="form-check-label position-relative"
                            htmlFor={`custom-price-1`}
                          >
                            {t("Custom Price")}
                          </label>
                        </div>
                        <div
                          className="d-flex justify-content-start align-items-center form-check me-3"
                        >
                          <input
                            className="form-check-input me-2"
                            type="radio"
                            name="custom-price"
                            id={`custom-price-0`}
                            checked={formik?.values?.priceType === "FREE"}
                            style={{ minWidth: "16px" }}
                            onChange={(event) => {
                              if(event?.target?.checked === true) {
                                formik.setFieldValue("price", 0)
                                formik.setFieldValue("vatRate", 0)
                                formik.setFieldValue("discountRate", 0)
                                formik.setFieldValue("priceFinal", 0)
                              }
                              formik.setFieldValue("priceType", "FREE")
                            }}
                          />
                          <label
                            className="form-check-label position-relative"
                            htmlFor={`custom-price-0`}
                          >
                            {t("Free Plan")}
                          </label>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {
                    formik?.values?.priceType === "CUSTOM" && (
                      <>
                        <Col lg={3}>
                          <div className="mb-3">
                            <Label htmlFor="price" className="form-label">
                              {t("Price")} <span className="text-danger">*</span>
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.price} 
                              placeholder={t("Price")}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              isAllowed={(values) => {
                                const { formattedValue, floatValue } = values;
                                return formattedValue === "" || (floatValue || 0) <= 9999999999;
                              }}
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                if(!floatValue) {
                                  formik.setFieldValue("priceFinal", 0);
                                  formik.setFieldValue("discountRate", 0);
                                }
                                
                                if (formik?.values?.discountRate) {
                                  const discountRate = formik?.values?.discountRate;
                                  const final = floatValue - (floatValue * ((discountRate || 0) / 100));
                                  formik.setFieldValue("priceFinal", Math.round(final * 100) / 100);
                                }else {
                                  formik.setFieldValue("priceFinal", floatValue);
                                }

                                formik.setFieldValue(
                                  "price",
                                  floatValue
                                )
                              }
                              }
                            />
                            {formik.touched.price && formik.errors.price ? (
                              <div className="text-danger mt-2">
                                {formik.errors.price}
                              </div>
                            ) : null}
                          </div>
                        </Col>

                        <Col lg={3}>
                          <div className="mb-3">
                            <Label htmlFor="discountRate" className="form-label">
                              {`${t("Discount Rate")} (%)`}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.discountRate} 
                              placeholder={`${t("Discount Rate")} (%)`}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              isAllowed={(values) => {
                                const { formattedValue, floatValue } = values;
                                return formattedValue === "" || (floatValue || 0) <= 100;
                              }}
                              disabled={!formik?.values?.price}
                              onFocus={() => setTouchedDiscountRate(true)}
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                                formik.setFieldValue("discountRate", floatValue)

                                if (touchedDiscountRate && formik?.values?.price) {
                                  const price = formik?.values?.price;
                                  const final = price - (price * ((floatValue || 0) / 100));
                                  formik.setFieldValue("priceFinal", Math.round(final * 100) / 100);
                                }
                              }}
                            />
                           
                            {formik.touched.discountRate && formik.errors.discountRate ? (
                              <div className="text-danger mt-2">
                                {formik.errors.discountRate}
                              </div>
                            ) : null}
                          </div>
                        </Col>

                        <Col lg={3}>
                          <div className="mb-3">
                            <Label htmlFor="priceFinal" className="form-label">
                              {t("Final Price")}
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.priceFinal} 
                              placeholder={t("Final Price")}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              isAllowed={(values) => {
                                const { formattedValue, floatValue } = values;
                                return formattedValue === "" || (floatValue || 0) <= 9999999999;
                              }}
                              disabled={true}
                              onValueChange={(values: any) => {
                                // const { floatValue } = values;
                                // formik.setFieldValue("priceFinal", floatValue);

                                // if (touchedPriceFinal && formik?.values?.price) {
                                //   const price = formik?.values?.price;
                                //   const discount = price > 0 && floatValue != null
                                //     ? Math.round(((price - floatValue) / price) * 100 * 100) / 100
                                //     : 0;

                                //   formik.setFieldValue("discountRate", discount);
                                // }
                              }}
                            />
                            {formik.touched.priceFinal && formik.errors.priceFinal ? (
                              <div className="text-danger mt-2">
                                {formik.errors.priceFinal}
                              </div>
                            ) : null}
                          </div>
                        </Col>

                        <Col lg={3}>
                          <div className="mb-3">
                            <Label htmlFor="vatRate" className="form-label">
                              {t("VAT %")} <span className="text-danger">*</span>
                            </Label>
                            <NumericFormat
                              className="form-control" 
                              value={formik?.values?.vatRate} 
                              placeholder={t("VAT %")}
                              allowLeadingZeros 
                              allowNegative={false}
                              thousandSeparator="," 
                              isAllowed={(values) => {
                                const { formattedValue, floatValue } = values;
                                return formattedValue === "" || (floatValue || 0) <= 100;
                              }}
                              onValueChange={(values: any) => {
                                const {floatValue} = values;
                               
                                formik.setFieldValue("vatRate", floatValue)
                              }
                              }
                            />
                            {formik.touched.vatRate && formik.errors.vatRate ? (
                              <div className="text-danger mt-2">
                                {formik.errors.vatRate}
                              </div>
                            ) : null}
                          </div>
                        </Col>
                      </>
                    )
                  }

                  <Col lg={3}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Currency")} <span className="text-danger">*</span>
                      </Label>

                      <DropdownStatusNew
                        name="currency"
                        dataList={LIST_CURRENCY_OPTION || []}
                        placeholder={`${t("Select currency")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik.values.currency}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("currency", e?.value);
                        }}
                      />

                      {formik.touched.currency && formik.errors.currency ? (
                        <div className="text-danger mt-2">
                          {formik.errors.currency}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <PlanFeatureArray formik={formik} />
                    </div>
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Status")} <span className="text-danger">*</span>
                      </Label>

                      <DropdownStatusNew
                        name="status"
                        dataList={STATUS_ACTIVE_DISABLE_OPTION || []}
                        placeholder={`${t("Select status")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik.values.isActive}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("isActive", e);
                        }}
                        colors={COLOR_STATUS_ACTIVE_DISABLE}
                      />
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="hstack gap-2 justify-content-end">
                      {!isShowDetail && (
                        <button type="submit" className="btn btn-primary fs-14">
                          {isLoading ? (
                            <Spinner size="sm" className="me-2"></Spinner>
                          ) : !!id ? (
                            <i className="ri-login-circle-line align-bottom me-2 "></i>
                          ) : (
                            <i className="ri-add-fill align-bottom me-2"></i>
                          )}
                          {id ? t("Update Plan") : t("Create Plan")}
                        </button>
                      )}
                    </div>
                  </Col>
                </Row>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PlanForm;
